import { CloseOutlined } from '@ant-design/icons';
import { Button, Drawer, Form } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import {
  useDiscountAllocation,
  useSaveDiscountAllocation,
} from 'app/hooks/data/core/extranet/pack/useDiscountAllocation';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { inventoryStore } from '../../InventoryStore';

import styles from './PricingDrawer.module.scss';
import { roundToOneDecimal } from './_components/ExperienceDiscount/_components/experiencePricing';
import { PricingContent } from './_components/PricingContent';
import { PricingFooter } from './_components/PricingFooter';
import type { DefaultDiscountMode } from './_components/RoomDiscount/hooks/useRoomDiscountLogic';

type PricingConfigurationFormValues = {
  averageRoomPrice: number;
  defaultDiscountMode: DefaultDiscountMode;
  defaultNightRoomDiscount: number;
  defaultNightRoomDiscountDays?: Partial<Record<DayOfWeek, number>>;
  lastMinuteNightRoomDiscountRate: number;
  lastMinuteNightRoomDiscountDays: number;
  overrideManualData: boolean;
  [key: `experienceDiscount-${number}-${number}`]: number;
};

export const PricingDrawer = () => {
  const [form] = Form.useForm();
  const { hotelId, hotelInfo } = useContext(HotelContext);
  const { packsList } = useContext(ModernHotelContext);

  const { mutateAsync: savePricingConfiguration } = useSaveDiscountAllocation();

  const {
    isPricingDrawerOpened,
    setPricingDrawerOpen,
    setPackIdForPricingDrawer,
  } = useStore(
    inventoryStore,
    useShallow((state) => ({
      isPricingDrawerOpened: state.isPricingDrawerOpened,
      setPricingDrawerOpen: state.setPricingDrawerOpen,
      setPackIdForPricingDrawer: state.setPackIdForPricingDrawer,
    }))
  );

  const { setOpenPricingFromTask } = useStore(
    inventoryStore,
    useShallow((state) => ({
      setOpenPricingFromTask: state.setOpenPricingFromTask,
    }))
  );

  const { data: averageRoomPrice } = useDiscountAllocation(
    hotelId,
    isPricingDrawerOpened
  );

  const seededAverageRoomPrice =
    averageRoomPrice?.average !== null &&
    averageRoomPrice?.average !== undefined
      ? Math.round(averageRoomPrice.average)
      : undefined;

  const initialExperienceDiscounts = useMemo(
    () =>
      packsList.reduce((acc, pack) => {
        pack.packExperiences.forEach((d) => {
          acc[`experienceDiscount-${pack.id}-${d.experienceId}`] =
            roundToOneDecimal((d.discount ?? 0) * 100);
        });

        return acc;
      }, {} as Record<string, number>),
    [packsList]
  );

  const defaultNightRoomDiscountDaysPercent = useMemo(() => {
    const config = hotelInfo?.defaultNightRoomDiscountConfig;

    if (config?.type !== 'DAILY') {
      return undefined;
    }

    return Object.entries(config.days).reduce((acc, [day, rateObj]) => {
      acc[day as DayOfWeek] = Math.round(rateObj.rate * 100);

      return acc;
    }, {} as Partial<Record<DayOfWeek, number>>);
  }, [hotelInfo]);

  const seededDefaultNightRoomDiscount = useMemo(() => {
    const config = hotelInfo?.defaultNightRoomDiscountConfig;

    if (config?.type === 'CONSTANT') {
      return Math.round(config.configuration.rate * 100);
    }

    if (config?.type === 'DAILY') {
      const rates = Object.values(config.days).map((day) => day.rate);

      return Math.round(
        (rates.reduce((sum, rate) => sum + rate, 0) / rates.length) * 100
      );
    }

    if (
      hotelInfo?.defaultNightRoomDiscount !== null &&
      hotelInfo?.defaultNightRoomDiscount !== undefined
    ) {
      return Math.round(hotelInfo.defaultNightRoomDiscount * 100);
    }

    return undefined;
  }, [hotelInfo]);

  const [isDrawerFullyOpen, setIsDrawerFullyOpen] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      averageRoomPrice: seededAverageRoomPrice,
      defaultDiscountMode: defaultNightRoomDiscountDaysPercent
        ? 'DAILY'
        : 'CONSTANT',
      defaultNightRoomDiscountDays: defaultNightRoomDiscountDaysPercent,
      lastMinuteNightRoomDiscountDays:
        hotelInfo?.lastMinuteNightRoomDiscountDays,
      lastMinuteNightRoomDiscountRate:
        hotelInfo?.lastMinuteNightRoomDiscountRate !== null &&
        hotelInfo?.lastMinuteNightRoomDiscountRate !== undefined
          ? Math.round(hotelInfo.lastMinuteNightRoomDiscountRate * 100)
          : undefined,
      defaultNightRoomDiscount: seededDefaultNightRoomDiscount,
      ...initialExperienceDiscounts,
    });
  }, [
    seededAverageRoomPrice,
    seededDefaultNightRoomDiscount,
    form,
    hotelInfo,
    initialExperienceDiscounts,
    defaultNightRoomDiscountDaysPercent,
  ]);

  const handleClose = () => {
    amplitudeTrack('Cancel Pricing Configuration');
    form.resetFields();
    setPricingDrawerOpen(false);
    setPackIdForPricingDrawer(undefined);
    setOpenPricingFromTask(false);
  };

  const handleOnFinish = async (values: PricingConfigurationFormValues) => {
    const mergedValues = {
      ...initialExperienceDiscounts,
      ...values,
      lastMinuteNightRoomDiscountRate: form.getFieldValue(
        'lastMinuteNightRoomDiscountRate'
      ),
      lastMinuteNightRoomDiscountDays: form.getFieldValue(
        'lastMinuteNightRoomDiscountDays'
      ),
    } as PricingConfigurationFormValues;

    const packs = packsList.map((pack) => ({
      packId: pack.id,
      experienceDiscounts: pack.packExperiences.map((pe) => ({
        experienceId: pe.experienceId,
        discount:
          (mergedValues[`experienceDiscount-${pack.id}-${pe.experienceId}`] ??
            0) / 100,
      })),
    }));

    const dailyDays = Object.values(DAY_OF_WEEK).reduce((acc, day) => {
      acc[day] = (mergedValues.defaultNightRoomDiscountDays?.[day] ?? 0) / 100;

      return acc;
    }, {} as Record<DayOfWeek, number>);

    const dailyRates = Object.values(dailyDays);

    const isUniformDaily = dailyRates.every((rate) => rate === dailyRates[0]);

    const defaultNightRoomDiscount =
      mergedValues.defaultDiscountMode === 'DAILY' && !isUniformDaily
        ? {
            type: 'DAILY' as const,
            days: dailyDays,
          }
        : {
            type: 'CONSTANT' as const,
            rate:
              mergedValues.defaultDiscountMode === 'DAILY'
                ? dailyRates[0] ?? 0
                : mergedValues.defaultNightRoomDiscount / 100,
          };

    await savePricingConfiguration({
      hotelId,
      averageRoomPrice: mergedValues.averageRoomPrice,
      defaultNightRoomDiscount,
      lastMinuteNightRoomDiscountRate:
        mergedValues.lastMinuteNightRoomDiscountRate !== null &&
        mergedValues.lastMinuteNightRoomDiscountRate !== undefined
          ? mergedValues.lastMinuteNightRoomDiscountRate / 100
          : undefined,
      lastMinuteNightRoomDiscountDays:
        mergedValues.lastMinuteNightRoomDiscountDays ?? undefined,
      packs,
      overrideManualData: mergedValues.overrideManualData,
    });

    amplitudeTrack('Save Pricing Configuration');

    handleClose();
  };

  return (
    <Form
      name="discount-allocation-form"
      form={form}
      onFinish={handleOnFinish}
      initialValues={{
        averageRoomPrice: seededAverageRoomPrice,
        defaultDiscountMode: defaultNightRoomDiscountDaysPercent
          ? 'DAILY'
          : 'CONSTANT',
        defaultNightRoomDiscountDays: defaultNightRoomDiscountDaysPercent,
        lastMinuteNightRoomDiscountDays:
          hotelInfo?.lastMinuteNightRoomDiscountDays,
        lastMinuteNightRoomDiscountRate:
          hotelInfo?.lastMinuteNightRoomDiscountRate !== null &&
          hotelInfo?.lastMinuteNightRoomDiscountRate !== undefined
            ? Math.round(hotelInfo.lastMinuteNightRoomDiscountRate * 100)
            : undefined,
        defaultNightRoomDiscount: seededDefaultNightRoomDiscount,
        overrideManualData: false,
        ...initialExperienceDiscounts,
      }}
    >
      <Drawer
        title={
          <div className={styles.pricingHeader}>
            <p className={styles.title}>Configure packages pricing rules</p>
            <p className={styles.subtitle}>
              Define discounts on room and activities to maximise your packages
              visibility and revenue
            </p>
          </div>
        }
        afterOpenChange={setIsDrawerFullyOpen}
        className={styles.pricingDrawerConfig}
        width="800"
        closable={false}
        extra={
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClose}
            className={styles.closeButton}
          />
        }
        open={isPricingDrawerOpened}
        bodyStyle={{ padding: 0 }}
        onClose={handleClose}
        destroyOnClose
        footer={
          <PricingFooter handleClose={handleClose} handleSave={form.submit} />
        }
      >
        <PricingContent isDrawerFullyOpen={isDrawerFullyOpen} />
      </Drawer>
    </Form>
  );
};
